import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  useRedeemRewardMutation,
  useResendRewardMutation,
} from "@apis/rewards/useRewards"
import { useAccount } from "@apis/accounts/useAccounts"
import { accountKeys } from "@apis/accounts/queryKeys"
import type { IAccountReward } from "@apis/rewards/types"
import { useHasPermissions } from "@hooks/useHasPermissions"
import { useSortingState } from "@hooks/useSortingState"
import { Button } from "@components/ui/button"
import { DataTable } from "@components/DataTable/DataTable"
import { ReissueRewardForm, type TReissueRewardForm } from "./ReissueRewardForm"
import {
  bonusHistoryColumns,
  loyaltyProgramHistoryColumns,
} from "./utils/tableColumnSchema"
import { RewardStatusBar } from "./RewardStatusBar"
import { ReferralDetail } from "../Referrals/ReferralDetail"
import { RAF_TEMPLATES } from "@apis/accounts/utils"

type TRewardDetailProps = {
  reward: IAccountReward
  account_id: string
}

export function RewardDetail({ reward, account_id }: TRewardDetailProps) {
  const queryClient = useQueryClient()
  const { canResendReward } = useHasPermissions()
  const getAccountQuery = useAccount(account_id)
  const resendRewardMutation = useResendRewardMutation()
  const redeemRewardMutation = useRedeemRewardMutation()

  const [showRedeemReward, setShowRedeemReward] = useState(false)
  const [showResendReward, setShowResendReward] = useState(false)

  const sorting = useSortingState(
    reward.template === "loyalty_program"
      ? [{ id: "invoice_dt", desc: true }]
      : [],
  )

  if (RAF_TEMPLATES.includes(reward.template as string)) {
    return <ReferralDetail account_id={account_id} />
  }

  const onSubmitResendReward = (values: TReissueRewardForm) => {
    resendRewardMutation.mutate(
      {
        reward_guid: reward.reward_guid,
        data: {
          service_zip_code: getAccountQuery.data?.service_zip_code as string,
          fulfillment_channel: values.channel_code,
        },
      },
      {
        onSuccess: () => {
          toast.success("Successfully resent reward!")
          setShowResendReward(false)
        },
        onError: (error) => toast.error(`Error resending reward! ${error}`),
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountRewards(account_id),
          })
        },
      },
    )
  }

  const onSubmitRedeemReward = (values: TReissueRewardForm) => {
    redeemRewardMutation.mutate(
      {
        reward_guid: reward.reward_guid,
        data: {
          reward_guid: reward.reward_guid,
          service_zip_code: getAccountQuery.data?.service_zip_code as string,
          fulfillment_channel: values.channel_code,
        },
      },
      {
        onSuccess: () => {
          toast.success("Successfully redeemed reward!")
          setShowRedeemReward(false)
        },
        onError: (error) => toast.error(`Failed to redeem reward: ${error}`),
        onSettled: () => {
          queryClient.invalidateQueries({
            queryKey: accountKeys.accountRewards(account_id),
          })
        },
      },
    )
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-6">
      <div>
        <p>{reward.description}</p>
        {reward.fulfillment_batch_id && (
          <p>Fulfillment Batch ID: {reward.fulfillment_batch_id}</p>
        )}
      </div>

      <div className="tw:md:max-w-3xl">
        <RewardStatusBar reward={reward} />
      </div>

      {reward.can_redeem && (
        <div className="tw:flex tw:flex-col tw:items-start tw:gap-3">
          <p>
            This reward is eligible to be redeemed. Click 'Redeem' below to
            redeem for the customer.
          </p>
          {!showRedeemReward ? (
            <Button onClick={() => setShowRedeemReward(true)}>
              Redeem {reward.reward_amount}
            </Button>
          ) : (
            <ReissueRewardForm
              onCancel={setShowRedeemReward}
              reward={reward}
              onSubmit={onSubmitRedeemReward}
              isPending={redeemRewardMutation.isPending}
            />
          )}
        </div>
      )}

      {reward.can_resend && canResendReward && (
        <div className="tw:flex tw:flex-col tw:items-start tw:gap-3">
          <p>
            This reward is eligible to be resent. Click 'Resend' below to resend
            for the customer.
          </p>

          {!showResendReward ? (
            <Button onClick={() => setShowResendReward(true)} className="">
              Resend {reward.reward_amount}
            </Button>
          ) : (
            <ReissueRewardForm
              onCancel={setShowResendReward}
              reward={reward}
              onSubmit={onSubmitResendReward}
              isPending={resendRewardMutation.isPending}
            />
          )}
        </div>
      )}

      <div>
        <h3 className="tw:text-cent">History</h3>
        <DataTable
          columns={
            reward.template === "loyalty_program"
              ? loyaltyProgramHistoryColumns
              : bonusHistoryColumns
          }
          data={reward.accruals}
          {...sorting}
        />
      </div>
    </div>
  )
}
